import { useCallback, useState } from "react";
import { useUtilityAdminTranslation } from "../../../../hooks/useUtilityAdminTranslation";
import { toast } from "react-toastify";
import { RetryRefundResult } from "../../../../model/refunds/refund";
import { useRetryRefundMutation } from "../../../../service/refund-service";
import { getErrorMessage } from "../../../../utils/error-handler";

/**
 * The "Retry Refund" flow shared by the list and the detail page: pick a case, confirm in the
 * modal, submit, and tell the admin how the attempt ended. The hub answers synchronously with
 * the attempt's outcome, so the toast carries it rather than a generic "submitted".
 */
export const useRetryRefund = () => {
	const { t } = useUtilityAdminTranslation();
	const [retryRefund, { isLoading: isRetrying }] = useRetryRefundMutation();
	const [target, setTarget] = useState<{ transactionId: string; reference: string } | null>(null);

	const notify = useCallback(
		(result: RetryRefundResult) => {
			if (result.status === "Refunded") {
				toast.success(t("refunds.retryResult.refunded"));
			} else if (result.status === "InProgress") {
				toast.info(t("refunds.retryResult.pending"));
			} else {
				toast.error(t("refunds.retryResult.failed", { reason: result.failureReason ?? "" }));
			}
		},
		[t]
	);

	const confirm = useCallback(() => {
		if (!target) return;

		retryRefund(target.transactionId)
			.unwrap()
			.then(notify)
			.catch(err => toast.error(getErrorMessage(err?.data)))
			.finally(() => setTarget(null));
	}, [target, retryRefund, notify]);

	return {
		target,
		isRetrying,
		requestRetry: (refund: { transactionId: string; reference: string }) => setTarget(refund),
		cancel: () => setTarget(null),
		confirm,
	};
};
